/**
 * NotificationsPage
 *
 * Main page for creating and sending notification campaigns.
 * Two-column layout: form (left) + preview/summary (right).
 *
 * @see implementation_plan.md
 */

import { useState } from "react";
import { Bell, CheckCircle, RotateCcw, BarChart3, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../../shared/components/ui/Button";
import { SegmentSelector } from "../components/SegmentSelector";
import { NotificationForm } from "../components/NotificationForm";
import { CardPreview } from "../components/CardPreview";
import { SendSummary } from "../components/SendSummary";
import { ConfirmModal } from "../components/ConfirmModal";
import { useNotifications } from "../hooks/useNotifications";

// NOTE: Reemplazar con useAuth().brandId cuando el contexto de auth esté integrado.
const MOCK_BRAND_ID = "brand-demo";

export function NotificationsPage() {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const {
    segments,
    segmentsLoading,
    segmentsError,
    reloadSegments,
    selectedSegment,
    form,
    updateForm,
    resetForm,
    isValid,
    imagePreview,
    setImagePreview,
    sendNotification,
    sending,
    error,
    success,
  } = useNotifications(MOCK_BRAND_ID);

  const handleSendClick = () => {
    if (!isValid) return;
    setShowConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    const result = await sendNotification();
    if (result) {
      setShowConfirmModal(false);
    }
  };

  const handleScheduleClick = () => {
    // For now, just show a placeholder. Full scheduling UI in Phase 2.
    setShowScheduleModal(true);
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen p-6 md:p-8 animate-fade-in">
        <div className="mx-auto max-w-2xl text-center py-20">
          <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            ¡Notificación Enviada!
          </h2>
          <p className="text-slate-500 text-lg mb-8">
            Tu notificación fue enviada exitosamente a{" "}
            <span className="font-semibold text-violet-600">
              {selectedSegment?.lastKnownCount.toLocaleString()}
            </span>{" "}
            usuarios del segmento "{selectedSegment?.name}".
          </p>
          <Button variant="primary" size="lg" onClick={resetForm}>
            <RotateCcw size={16} />
            Enviar otra notificación
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-2">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                Push puntual
              </h1>
            </div>
            <p className="text-slate-500 text-base mt-1">
              Push notification de una sola vez: elegí segmento, redactá y
              enviá ahora. Para automatizaciones que se repiten (cumpleaños,
              carrito, reactivación) andá a <strong>Campañas</strong> en el
              menú lateral.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/notifications/analytics">
              <Button variant="secondary">
                <BarChart3 size={16} />
                Métricas
              </Button>
            </Link>
            <Button variant="secondary" onClick={resetForm} disabled={sending}>
              <RotateCcw size={16} />
              Limpiar
            </Button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
            <Bell size={18} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {/* ── Stepper (H18) ── */}
        <StepProgress
          hasSegment={!!selectedSegment}
          hasContent={!!(form.title || form.body)}
          isValid={isValid}
        />

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column - 60% */}
          <div className="lg:col-span-3 space-y-6">
            {/* Segment Selector */}
            <SegmentSelector
              segments={segments}
              selectedSegment={selectedSegment}
              loading={segmentsLoading}
              error={segmentsError}
              onRetry={reloadSegments}
              onSelect={(id) => updateForm({ segmentId: id })}
            />

            {/* Notification Form */}
            <NotificationForm
              title={form.title}
              body={form.body}
              link={form.link}
              imagePreview={imagePreview}
              actionButtons={form.actionButtons}
              onTitleChange={(title) => updateForm({ title })}
              onBodyChange={(body) => updateForm({ body })}
              onLinkChange={(link) => updateForm({ link })}
              onImageChange={setImagePreview}
              onActionButtonsChange={(actionButtons) =>
                updateForm({ actionButtons })
              }
            />
          </div>

          {/* Right Column - 40% */}
          <div className="lg:col-span-2 space-y-6">
            {/* Preview */}
            <CardPreview
              title={form.title}
              body={form.body}
              imagePreview={imagePreview}
              actionButtons={form.actionButtons}
            />

            {/* Send Summary */}
            <SendSummary
              selectedSegment={selectedSegment}
              isValid={isValid}
              sending={sending}
              onSend={handleSendClick}
              onSchedule={handleScheduleClick}
            />
          </div>
        </div>
      </div>

      {/* Confirm Send Modal */}
      {showConfirmModal && selectedSegment && (
        <ConfirmModal
          recipientCount={selectedSegment.lastKnownCount}
          segmentName={selectedSegment.name}
          title={form.title}
          onConfirm={handleConfirmSend}
          onCancel={() => setShowConfirmModal(false)}
          sending={sending}
        />
      )}

      {/* Schedule Modal Placeholder */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-6 text-center">
            <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Bell size={24} className="text-violet-600" />
            </div>
            <h3 className="font-semibold text-lg text-slate-900 mb-2">
              Programar Envío
            </h3>
            <p className="text-slate-500 text-sm mb-5">
              La funcionalidad de programación estará disponible próximamente.
            </p>
            <Button
              variant="secondary"
              onClick={() => setShowScheduleModal(false)}
              className="w-full"
            >
              Entendido
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Stepper progress (H18) ─── */
function StepProgress({
  hasSegment,
  hasContent,
  isValid,
}: {
  hasSegment: boolean;
  hasContent: boolean;
  isValid: boolean;
}) {
  const steps = [
    { label: "Elegí segmento", done: hasSegment },
    { label: "Redactá", done: hasContent },
    { label: "Revisá preview", done: hasContent && hasSegment },
    { label: "Enviá", done: isValid },
  ];

  // Active step = first not-done step
  const activeIdx = steps.findIndex((s) => !s.done);
  const currentStep = activeIdx === -1 ? steps.length : activeIdx;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-6 py-4">
      <div className="flex items-center gap-0">
        {steps.map((step, i) => {
          const done = step.done;
          const active = i === currentStep;
          const isLast = i === steps.length - 1;
          return (
            <div key={step.label} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                {/* Circle */}
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 text-xs font-bold flex-shrink-0 ${
                    done
                      ? "bg-violet-600 text-white shadow-sm shadow-violet-300"
                      : active
                      ? "bg-violet-100 text-violet-700 ring-2 ring-violet-400 ring-offset-1"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {done ? <Check size={13} /> : i + 1}
                </div>
                {/* Label */}
                <span
                  className={`text-[10px] font-medium whitespace-nowrap ${
                    done
                      ? "text-violet-600"
                      : active
                      ? "text-slate-900"
                      : "text-slate-400"
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {/* Connector line */}
              {!isLast && (
                <div
                  className={`flex-1 h-0.5 mx-2 mb-4 rounded-full transition-colors duration-300 ${
                    done ? "bg-violet-400" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default NotificationsPage;
